import type { ReactNode } from 'react';

import type {
    EdlpEcomPromotion,
    EcomPromotion,
    FixedEcomPromotion,
    MultiBuyEcomPromotion,
    PercentageEcomPromotion,
} from '../../../models/domain/ecommerce/promotion/promotion';

import { useIsUserAMember } from '../../../hooks/useUserProfile';
import {
    isEdlpPromotion,
    isFixedPricePromotion,
    isMultiBuyPromotion,
    isPercentagePromotion,
} from '../../../models/domain/ecommerce/promotion/promotionMappers';

interface PromotionPickerParams {
    promo?: EcomPromotion;
    quantity?: number;

    /**
     * If true, the member promotion will only be shown if the user is a member.
     */
    checkUserMemberContext: boolean;
    debug?: string;
}

interface ProductPricePromotionPickerProps extends PromotionPickerParams {
    renderPercentagePromotion?: (promo: PercentageEcomPromotion) => ReactNode;
    renderMultiBuyPromotion: (promo: MultiBuyEcomPromotion) => ReactNode;
    renderNotApplicablePromotion?: () => ReactNode;
    renderFixedPromotion?: (promo: FixedEcomPromotion) => ReactNode;
    renderEdlpPromotion?: (promo: EdlpEcomPromotion) => ReactNode;
    renderDefault: () => ReactNode;
}

export const pickProductPricePromotion = (
    params: PromotionPickerParams & {
        userIsMember?: boolean;
    },
) => {
    const log = (msg: string) => {
        if (params.debug) {
            // eslint-disable-next-line no-console
            console.logDev('pickProductPricePromotion', msg);
        }
    };

    if (params.checkUserMemberContext && params.promo?.isMemberPrice && !params.userIsMember) {
        log('notApplicable');
        return { type: 'notApplicable' } as const;
    }

    if (params.promo && isPercentagePromotion(params.promo)) {
        log('percentage');
        return { type: 'percentage', promotion: params.promo } as const;
    }

    if (params.promo && isFixedPricePromotion(params.promo)) {
        log('fixed');
        return { type: 'fixed', promotion: params.promo } as const;
    }

    if (params.promo && isEdlpPromotion(params.promo)) {
        log('edlp');
        return { type: 'edlp', promotion: params.promo } as const;
    }

    if (params.promo && isMultiBuyPromotion(params.promo)) {
        if (params.quantity == null) {
            log('multiBuy, quantity is null');
            // no current quantity is provided, so we cannot determine if its eligible for promo or not, by default we say it is
            return { type: 'multiBuy', promotion: params.promo } as const;
        }

        if (
            params.promo.numberOfProductRequired != null &&
            params.quantity >= params.promo.numberOfProductRequired
        ) {
            log('multiBuy, quantity not null');
            return { type: 'multiBuy', promotion: params.promo } as const;
        }

        log('notApplicable, quantity not null');
        return { type: 'notApplicable' } as const;
    }

    log('default');
    return { type: 'default' } as const;
};

const useProductPricePromotionPicker = (props: ProductPricePromotionPickerProps) => {
    const userIsMember = useIsUserAMember();

    const result = pickProductPricePromotion({
        promo: props.promo,
        quantity: props.quantity,
        checkUserMemberContext: props.checkUserMemberContext,
        userIsMember,
        debug: props.debug,
    });

    switch (result.type) {
        case 'notApplicable':
            return props.renderNotApplicablePromotion?.() || props.renderDefault();
        case 'percentage':
            return (
                props.renderPercentagePromotion?.(result.promotion as PercentageEcomPromotion) ||
                props.renderDefault()
            );
        case 'fixed':
            return (
                props.renderFixedPromotion?.(result.promotion as FixedEcomPromotion) ||
                props.renderDefault()
            );
        case 'edlp':
            return (
                props.renderEdlpPromotion?.(result.promotion as EdlpEcomPromotion) ||
                props.renderDefault()
            );
        case 'multiBuy':
            return props.renderMultiBuyPromotion(result.promotion as MultiBuyEcomPromotion);
        case 'default':
        default:
            return props.renderDefault();
    }
};

const ProductPricePromotionPicker = (props: ProductPricePromotionPickerProps) => {
    return useProductPricePromotionPicker(props);
};

export default ProductPricePromotionPicker;
